import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
      checked ? 'bg-violet-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const SettingsSection = ({ icon, title, description, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="px-6 py-5 border-b border-gray-100" style={{ background: 'linear-gradient(to right, rgba(245,138,84,0.06), rgba(224,82,138,0.06))' }}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(to right, #F58A54, #E0528A)' }}>
          <Icon name={icon} size={18} color="white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
    </div>
    <div className="divide-y divide-gray-50">{children}</div>
  </div>
);

const SettingsRow = ({ label, description, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
    <div className="flex-1 min-w-0 pr-4">
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
  </div>
);

const UserSettings = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    email_marketing: true,
    email_product_updates: true,
    email_quote_alerts: true,
    email_weekly_digest: false,
    notif_new_quotes: true,
    notif_milestone_updates: true,
    notif_messages: true,
    notif_vendor_activity: false,
    notif_system_alerts: true,
    data_opt_out: false,
    connected_google: false,
    connected_facebook: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login-sign-up');
      return;
    }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('user_profiles')?.select('email_marketing, email_product_updates, email_quote_alerts, email_weekly_digest, notif_new_quotes, notif_milestone_updates, notif_messages, notif_vendor_activity, notif_system_alerts, data_opt_out, connected_google, connected_facebook')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      if (data) {
        setSettings({
          email_marketing: data?.email_marketing ?? true,
          email_product_updates: data?.email_product_updates ?? true,
          email_quote_alerts: data?.email_quote_alerts ?? true,
          email_weekly_digest: data?.email_weekly_digest ?? false,
          notif_new_quotes: data?.notif_new_quotes ?? true,
          notif_milestone_updates: data?.notif_milestone_updates ?? true,
          notif_messages: data?.notif_messages ?? true,
          notif_vendor_activity: data?.notif_vendor_activity ?? false,
          notif_system_alerts: data?.notif_system_alerts ?? true,
          data_opt_out: data?.data_opt_out ?? false,
          connected_google: data?.connected_google ?? false,
          connected_facebook: data?.connected_facebook ?? false,
        });
      }
    } catch (err) {
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => (value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          email_marketing: settings?.email_marketing,
          email_product_updates: settings?.email_product_updates,
          email_quote_alerts: settings?.email_quote_alerts,
          email_weekly_digest: settings?.email_weekly_digest,
          notif_new_quotes: settings?.notif_new_quotes,
          notif_milestone_updates: settings?.notif_milestone_updates,
          notif_messages: settings?.notif_messages,
          notif_vendor_activity: settings?.notif_vendor_activity,
          notif_system_alerts: settings?.notif_system_alerts,
          data_opt_out: settings?.data_opt_out,
          connected_google: settings?.connected_google,
          connected_facebook: settings?.connected_facebook,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Sync data_opt_out to localStorage for Footer modal
      localStorage.setItem('data_opt_out', String(settings?.data_opt_out));

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-36 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white transition-all duration-200"
              >
                <Icon name="ArrowLeft" size={18} />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            </div>
            <p className="text-gray-500 text-sm ml-11">
              Manage your preferences for {userProfile?.email || user?.email}
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-6">

            {/* Email Preferences */}
            <SettingsSection
              icon="Mail"
              title="Email Preferences"
              description="Choose which emails you'd like to receive"
            >
              <SettingsRow
                label="Marketing & Promotions"
                description="Tips, offers, and news about Stackd features"
                checked={settings?.email_marketing}
                onChange={handleToggle('email_marketing')}
                disabled={false}
              />
              <SettingsRow
                label="Product Updates"
                description="New features, improvements, and release notes"
                checked={settings?.email_product_updates}
                onChange={handleToggle('email_product_updates')}
                disabled={false}
              />
              <SettingsRow
                label="Quote Alerts"
                description="Notifications when vendors submit or update quotes"
                checked={settings?.email_quote_alerts}
                onChange={handleToggle('email_quote_alerts')}
                disabled={false}
              />
              <SettingsRow
                label="Weekly Digest"
                description="A summary of your stack activity every week"
                checked={settings?.email_weekly_digest}
                onChange={handleToggle('email_weekly_digest')}
                disabled={false}
              />
            </SettingsSection>

            {/* Notification Controls */}
            <SettingsSection
              icon="Bell"
              title="Notification Controls"
              description="Control in-app notifications"
            >
              <SettingsRow
                label="New Quotes"
                description="When a vendor submits a new quote on your stack"
                checked={settings?.notif_new_quotes}
                onChange={handleToggle('notif_new_quotes')}
                disabled={false}
              />
              <SettingsRow
                label="Milestone Updates"
                description="Progress changes on your project milestones"
                checked={settings?.notif_milestone_updates}
                onChange={handleToggle('notif_milestone_updates')}
                disabled={false}
              />
              <SettingsRow
                label="Messages"
                description="New messages from vendors or the Stackd team"
                checked={settings?.notif_messages}
                onChange={handleToggle('notif_messages')}
                disabled={false}
              />
              <SettingsRow
                label="Vendor Activity"
                description="When vendors view or interact with your stack"
                checked={settings?.notif_vendor_activity}
                onChange={handleToggle('notif_vendor_activity')}
                disabled={false}
              />
              <SettingsRow
                label="System Alerts"
                description="Important account and security notifications"
                checked={settings?.notif_system_alerts}
                onChange={handleToggle('notif_system_alerts')}
                disabled={false}
              />
            </SettingsSection>

            {/* Data & Privacy */}
            <SettingsSection
              icon="ShieldCheck"
              title="Data & Privacy"
              description="Control how your data is used"
            >
              <div className="px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Do Not Sell or Share My Personal Information</p>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, your data will be excluded from any third-party sharing or sale. This preference is saved to your account and applies across all devices.
                    </p>
                    {settings?.data_opt_out && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                        <Icon name="Check" size={11} />
                        Opted out — your data is excluded
                      </span>
                    )}
                  </div>
                  <ToggleSwitch
                    checked={settings?.data_opt_out}
                    onChange={handleToggle('data_opt_out')}
                  />
                </div>
              </div>
            </SettingsSection>

            {/* Connected Accounts */}
            <SettingsSection
              icon="Link"
              title="Connected Accounts"
              description="Manage third-party account connections"
            >
              {/* Google */}
              <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Google</p>
                    <p className="text-xs text-gray-500">
                      {settings?.connected_google ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle('connected_google')(!settings?.connected_google)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                    settings?.connected_google
                      ? 'bg-red-50 text-red-600 hover:bg-red-100' :'bg-violet-50 text-violet-700 hover:bg-violet-100'
                  }`}
                >
                  {settings?.connected_google ? 'Disconnect' : 'Connect'}
                </button>
              </div>

              {/* Facebook */}
              <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center shadow-sm">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Facebook</p>
                    <p className="text-xs text-gray-500">
                      {settings?.connected_facebook ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle('connected_facebook')(!settings?.connected_facebook)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                    settings?.connected_facebook
                      ? 'bg-red-50 text-red-600 hover:bg-red-100' :'bg-violet-50 text-violet-700 hover:bg-violet-100'
                  }`}
                >
                  {settings?.connected_facebook ? 'Disconnect' : 'Connect'}
                </button>
              </div>
            </SettingsSection>

          </div>

          {/* Save Button */}
          <div className="mt-8 flex items-center justify-between">
            <p className="text-xs text-gray-400">Changes are saved to your account across all devices.</p>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Saving…</span>
                </>
              ) : saveSuccess ? (
                <>
                  <Icon name="Check" size={16} color="white" />
                  <span>Saved!</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} color="white" />
                  <span>Save Preferences</span>
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserSettings;
